import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from "react";
import { authAPI } from "../services/api";
import { User, LoginRequest, RegisterRequest } from "../types";
import { useApp } from "./AppContext";

interface Tenant {
  tenant_id: string;
  company_name: string;
  domain?: string;
  subdomain?: string;
  subscription_plan: "free" | "basic" | "pro" | "enterprise";
  branding: {
    logo_url?: string;
    primary_color?: string;
    secondary_color?: string;
    company_description?: string;
  };
  settings: {
    timezone: string;
    currency: string;
    language: string;
    order_number_prefix?: string;
    customer_number_prefix?: string;
  };
  is_active: boolean;
}

interface AuthContextType {
  user: User | null;
  tenant: Tenant | null;
  currentSubdomain: string | null;
  currentTenantId: string;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (credentials: LoginRequest) => Promise<User>;
  register: (userData: RegisterRequest) => Promise<User>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  refreshTenant: () => Promise<void>;
  switchTenant: (tenantId: string) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [currentSubdomain, setCurrentSubdomain] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { setUser: setAppUser, setGlobalError, setGlobalSuccess } = useApp();

  const isAuthenticated = !!user;

  // Simple tenant extraction function
  const extractTenant = (): string => {
    const hostname = window.location.hostname;

    // Skip for localhost and IPs
    if (
      hostname === "localhost" ||
      hostname === "127.0.0.1" ||
      hostname.includes("localhost")
    ) {
      return "orderapp-frontendx"; // Default tenant
    }

    // Skip for direct domain access
    if (
      hostname === "orderapp-frontend.pages.dev" ||
      hostname === "orderapp-frontendx.pages.dev" ||
      hostname === "ordertrackingapp.com"
    ) {
      return "orderapp-frontendx"; // Default tenant
    }

    // Extract subdomain (e.g., "demo" from "demo.ordertrackingapp.com")
    const parts = hostname.split(".");
    if (parts.length > 2) {
      const subdomain = parts[0];
      // Skip www
      if (subdomain !== "www" && subdomain !== "api") {
        return subdomain;
      }
    }

    return "orderapp-frontendx"; // Default fallback
  };

  // Initialize tenant detection
  useEffect(() => {
    const tenant = extractTenant();
    setCurrentSubdomain(tenant);
  }, []);

  // Check if user is logged in on mount
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const token = localStorage.getItem("token");
        if (token) {
          const userData = await authAPI.getProfile();
          setUser(userData);
          setAppUser(userData);

          // Get tenant information
          await refreshTenant();
        }
      } catch (error) {
        console.error("Auth check failed:", error);
        localStorage.removeItem("token");
        localStorage.removeItem("user");
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setAppUser]);

  const login = async (credentials: LoginRequest) => {
    try {
      setIsLoading(true);
      const response = await authAPI.login(credentials);

      // Safe destructuring with default values
      const { user: userData, token } = response || { user: null, token: "" };

      if (!userData || !token) {
        throw new Error("Invalid response from server");
      }

      localStorage.setItem("token", token);
      localStorage.setItem("user", JSON.stringify(userData));

      setUser(userData);
      setAppUser(userData);

      setGlobalSuccess("Başarıyla giriş yapıldı");

      // Return the user data for immediate use
      return userData;
    } catch (error: any) {
      console.error("AuthContext: Login error details:", {
        message: error.message,
        response: error.response,
        data: error.response?.data,
      });
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const register = async (userData: RegisterRequest) => {
    try {
      setIsLoading(true);
      const response = await authAPI.register(userData);

      // Safe destructuring with default values
      const { user: newUser, token } = response || { user: null, token: "" };

      if (!newUser || !token) {
        throw new Error("Invalid response from server");
      }

      localStorage.setItem("token", token);
      localStorage.setItem("user", JSON.stringify(newUser));

      setUser(newUser);
      setAppUser(newUser);

      setGlobalSuccess("Hesap başarıyla oluşturuldu");

      // Return the user data for immediate use
      return newUser;
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.error ||
        error.message ||
        "Kayıt olurken hata oluştu";
      setGlobalError(errorMessage);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    try {
      await authAPI.logout();
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      setUser(null);
      setAppUser(null);
      setGlobalSuccess("Başarıyla çıkış yapıldı");
    }
  };

  const refreshUser = async () => {
    try {
      const userData = await authAPI.getProfile();
      setUser(userData);
      setAppUser(userData);
    } catch (error) {
      console.error("Failed to refresh user:", error);
      await logout();
    }
  };

  const refreshTenant = async () => {
    try {
      // Use current subdomain if available
      const subdomain = currentSubdomain;

      const response = await authAPI.getTenantInfo(subdomain);
      setTenant(response);
    } catch (error: any) {
      console.error("Failed to get tenant info:", error);

      // If tenant info fails, set a default tenant structure
      const defaultTenant: Tenant = {
        tenant_id: currentSubdomain || "default",
        company_name: "OrderApp",
        subscription_plan: "free",
        branding: {
          logo_url: undefined,
          primary_color: "#1976d2",
          secondary_color: "#dc004e",
          company_description: undefined,
        },
        settings: {
          timezone: "Europe/Istanbul",
          currency: "TRY",
          language: "tr",
          order_number_prefix: "ORD",
          customer_number_prefix: "CUST",
        },
        is_active: true,
      };

      setTenant(defaultTenant);
    }
  };

  const value: AuthContextType = {
    user,
    tenant,
    currentSubdomain,
    currentTenantId: tenant?.tenant_id || "",
    isAuthenticated,
    isLoading,
    login,
    register,
    logout,
    refreshUser,
    refreshTenant,
    switchTenant: (tenantId) => {
      // Implement tenant switching logic here
    },
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export default AuthContext;
